<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewMdas' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to MDAs">
        MDAs
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Edit MDA</p>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <div class="form-outer">
        <form @submit.prevent="handleSubmit">
          <div class="crud-form position-relative">

            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center">
                <h1 class="mb-0">MDA</h1>
              </div>

              <div class="mda-content-area position-relative">
                <div v-if="!isDataLoaded" class="loading-overlay text-center">
                  <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                  <p class="mt-2 mb-0 text-muted">Loading MDA details...</p>
                </div>

                <div v-if="isDataLoaded" class="form-body mt-3 mb-3">

                  <div v-if="errorMessage" class="alert alert-warning alert-dismissible fade show mx-3 mb-3"
                    role="alert">
                    {{ errorMessage }}
                    <button type="button" class="btn-close" @click="errorMessage = ''"></button>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label class="form-label">Name</label>
                    </div>
                    <div class="form-row-input">
                      <input type="text" class="form-control form-view-only-input" v-model="form.name" readonly />
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label class="form-label">Organisation Code</label>
                    </div>
                    <div class="form-row-input">
                      <input type="text" class="form-control form-view-only-input" v-model="form.orgCode" readonly />
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label class="form-label">Sub-sector</label>
                    </div>
                    <div v-if="subSectorFieldEditable" class="form-row-input d-flex align-items-center gap-2">
                      <select class="form-select editable" v-model="form.subSectorId">
                        <option value="" disabled>- Select Sub-sector -</option>
                        <option v-for="sub in subsectors" :key="sub.id" :value="sub.id">{{ sub.description }}</option>
                      </select>
                      <span class="material-symbols-outlined edit-icon-filled" title="Cancel edit"
                        @click="toggleEditField('subSector')">edit</span>
                    </div>
                    <div v-else class="form-row-input d-flex align-items-center gap-2">
                      <input type="text" class="form-control form-view-only-input" :value="subSectorLabel" readonly />
                      <span class="material-symbols-outlined edit-icon" title="Edit Sub-sector"
                        @click="toggleEditField('subSector')">edit</span>
                    </div>
                  </div>

                  <div class="form-row">
                    <div class="form-row-label">
                      <label class="form-label">Group</label>
                    </div>
                    <div v-if="groupTypeFieldEditable"
                      class="form-row-input d-flex align-items-center gap-2">
                      <select class="form-select editable" v-model="form.groupTypeId">
                        <option value="" disabled>- Select Group -</option>
                        <option v-for="group in groupTypes" :key="group.id" :value="group.id">{{ group.description }}
                        </option>
                      </select>
                      <span class="material-symbols-outlined edit-icon-filled" title="Cancel edit"
                        @click="toggleEditField('groupType')">edit</span>
                    </div>
                    <div v-else class="form-row-input d-flex align-items-center gap-2">
                      <input type="text" class="form-control form-view-only-input" :value="groupTypeLabel" readonly />
                      <span class="material-symbols-outlined edit-icon" title="Edit Group"
                        @click="toggleEditField('groupType')">edit</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div v-if="isDataLoaded" class="action-buttons-container">
              <div class="action-buttons-wrapper">
                <div class="action-button">
                  <button type="submit" class="btn btn-primary submit-btn" :disabled="loading || !isEdited">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>
                    {{ loading ? 'Saving...' : 'Save Changes' }}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2'
import DashboardLayout from '../layout/DashboardLayout.vue'
import { useRoute, useRouter } from 'vue-router'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const router = useRouter()
const id = route.params.id

const isDataLoaded = ref(false)
const loading = ref(false)
const errorMessage = ref('')

const subSectorFieldEditable = ref(false)
const groupTypeFieldEditable = ref(false)

const subsectors = ref([])
const groupTypes = ref([])

const original = reactive({
  subSectorId: '',
  groupTypeId: '',
})

const form = reactive({
  id: null,
  name: '',
  orgCode: '',
  subSectorId: '',
  groupTypeId: '',
  version: null,
  createdDate: null,
  lastModifiedDate: null,
  createdBy: null,
  lastModBy: null,
  userId: null,
})

const isEdited = computed(() => {
  return form.subSectorId !== original.subSectorId || form.groupTypeId !== original.groupTypeId
})

const subSectorLabel = computed(() => {
  const found = subsectors.value.find(s => s.id === form.subSectorId)
  return found ? found.description : form.subSectorId || 'N/A'
})

const groupTypeLabel = computed(() => {
  const found = groupTypes.value.find(g => g.id === form.groupTypeId)
  return found ? found.description : form.groupTypeId || 'N/A'
})

function toggleEditField(field) {
  if (field === 'subSector') {
    subSectorFieldEditable.value = !subSectorFieldEditable.value
  } else if (field === 'groupType') {
    groupTypeFieldEditable.value = !groupTypeFieldEditable.value
  }
}

const fetchMda = async () => {
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/eli/mda/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    form.id = data.id ?? null
    form.name = data.name || ''
    form.orgCode = data.orgCode || ''
    form.subSectorId = data.subSectorId || ''
    form.groupTypeId = data.groupTypeId || ''
    form.version = data.version ?? null
    form.createdDate = data.createdDate ?? null
    form.lastModifiedDate = data.lastModifiedDate ?? null
    form.createdBy = data.createdBy ?? null
    form.lastModBy = data.lastModBy ?? null
    form.userId = data.userId ?? null
    original.subSectorId = data.subSectorId || ''
    original.groupTypeId = data.groupTypeId || ''
  } catch (error) {
    console.error('Failed to fetch MDA:', error)
    errorMessage.value = 'Failed to load MDA details. Please try again.'
  } finally {
    isDataLoaded.value = true
  }
}

const fetchSubSectors = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/sub-sector`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    subsectors.value = response.data.map(s => ({
      id: s.id,
      description: s.description,
    })).sort((a, b) => a.description.localeCompare(b.description))
  } catch (error) {
    console.error('Failed to fetch sub-sectors:', error)
  }
}

const fetchGroupTypes = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/group-type`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    groupTypes.value = response.data.map(g => ({
      id: g.id,
      description: g.name,
    })).sort((a, b) => a.description.localeCompare(b.description))
  } catch (error) {
    console.error('Failed to fetch group types:', error)
  }
}

const handleSubmit = async () => {
  if (!isEdited.value) return

  const token = Cookies.get('auth_token')
  const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  const payload = {
    id: form.id,
    name: form.name,
    orgCode: form.orgCode,
    subSectorId: form.subSectorId,
    groupTypeId: form.groupTypeId,
    version: form.version,
    createdDate: form.createdDate,
    lastModifiedDate: form.lastModifiedDate,
    createdBy: form.createdBy,
    lastModBy: form.lastModBy,
    userId: form.userId,
  }

  loading.value = true
  try {
    await axios.put(`${root_api_url}/eli/mda/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    await Swal.fire({
      html: `${iconHtmlSuccess} <h1 style="font-weight:600;">Success</h1> <p>MDA Updated Successfully</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })

    original.subSectorId = form.subSectorId
    original.groupTypeId = form.groupTypeId
    subSectorFieldEditable.value = false
    groupTypeFieldEditable.value = false
    router.push({ name: 'ViewMdas' })
  } catch (error) {
    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Unexpected error occurred while updating MDA. Kindly contact administrator'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred while updating MDA. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    }
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await Promise.all([fetchSubSectors(), fetchGroupTypes()])
  await fetchMda()
  document.title = 'Edit MDA'
})
</script>

<style scoped>
.mda-content-area {
  min-height: 280px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

.form-outer {
  width: 100%;
  max-width: 800px;
}

.crud-form {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-header h1 {
  font-size: 1.5rem;
  color: #333;
}

/* Form Row Layout */
.form-row {
  display: flex;
  margin-bottom: 1.5rem;
  gap: 1rem;
  align-items: flex-start;
}

.form-row-label {
  flex: 0 0 30%;
  text-align: right;
  padding-top: 0.5rem;
}

.form-row-label label {
  font-weight: 600;
    color: var(--color);
  margin-bottom: 0;
}

.form-row-input {
  flex: 1;
}

/* Action Buttons Container */
.action-buttons-container {
  padding: 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--bg);
}

.action-buttons-wrapper {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin: 0 !important;
  padding: 0 !important;
}

.action-button {
  flex: 0 0 auto;
}

.action-button button {
  min-width: 140px;
  white-space: nowrap;
  display: block;
  margin: 0 auto !important;
  background-color: #3498db !important;
  border-color: #3498db !important;
  color: #fff !important;
}

.editable {
  border: 1px dashed #aaa;
  background-color:var(--table-bg-head);
}

.edit-icon {
  font-size: 16px;
  color: #aaa;
  cursor: pointer;
}

.edit-icon-filled {
  font-variation-settings:
    'FILL' 1,
    'wght' 400,
    'GRAD' 0,
    'opsz' 24;
}

/* Tablet Responsiveness (768px - 1024px) */
@media screen and (max-width: 1024px) {
  .form-outer {
    max-width: 100%;
  }

  .form-row-label {
    flex: 0 0 40%;
  }
}

/* Mobile Responsiveness (below 768px) */
@media screen and (max-width: 768px) {
  .form-header h1 {
    font-size: 1.25rem;
  }

  .form-row {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e9ecef;
  }

  .form-row-label {
    flex: none;
    text-align: left;
    padding-top: 0;
  }

  .form-row-label label {
    font-size: 0.875rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .form-row-input {
    flex: none;
    width: 100%;
  }

  .action-buttons-container {
    padding: 1rem;
  }

  .action-buttons-wrapper {
    flex-direction: column;
    gap: 0.75rem;
  }

  .action-button {
    width: 100%;
  }

  .action-button button {
    width: 100%;
    min-width: 100%;
  }

  .page-identifier {
    font-size: 0.875rem;
  }

  .page-identifier .material-symbols-outlined {
    font-size: 1.25rem;
  }
}

/* Small Mobile (below 480px) */
@media screen and (max-width: 480px) {
  .form-header h1 {
    font-size: 1.125rem;
  }

  .form-row {
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }

  .form-row-label label {
    font-size: 0.8125rem;
  }

  .action-buttons-container {
    padding: 0.75rem;
  }

  .btn {
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }
}

/* Print Styles */
@media print {
  .action-buttons-container,
  .page-identifier {
    display: none !important;
  }

  .form-outer {
    max-width: 100%;
    box-shadow: none;
  }

  .form-row {
    page-break-inside: avoid;
  }
}
</style>
